/**
 * PURPOSE: Cliente HTTP del módulo administrativo de Resultados de Atletismo (Hayllis V2).
 * RESPONSIBILITIES: Una función por endpoint de
 *   `/api/hayllis/v2/admin/athletics-results/events/:idevent/sports/:idsport/...` (ver
 *   backend_winscore/docs/atletismo-contract.md §4), desenvolviendo el sobre `{ ok, data }`.
 *
 *   FAIL CLOSED EN EL ESTADO. `getAthleticsResultsStatus` nunca lanza: ante cualquier error (404
 *   `FEATURE_DISABLED`, red caída, respuesta rara) devuelve TODO apagado. La pestaña simplemente no
 *   aparece; el backend sigue validando rol, compañía y evento en cada petición.
 *
 *   CONCURRENCIA. Toda escritura de una prueba manda `expectedRevision` (la `prueba.revision` que el
 *   operador está viendo). Un 409 `REVISION_CONFLICT` se relanza tal cual: la interfaz avisa y
 *   ofrece recargar, nunca reintenta.
 *
 *   ERRORES. Se relanza el error de axios sin aplanarlo para que la interfaz lea `code`/`details`
 *   (utils/athleticsResultsErrors.js).
 * COLLABORATORS: httpService.js, hooks/useAthleticsResults.js, hooks/useAthleticsResultsStatus.js.
 * ROUTE: winscore/src/services/athleticsResultsService.js
 */
import httpService from "./httpService.js";

export const ATHLETICS_RESULTS_ADMIN_BASE = "/api/hayllis/v2/admin/athletics-results";

export const ATHLETICS_RESULTS_STATUS_OFF = Object.freeze({
  enabled: false,
  reason: null,
  canWrite: false,
  canOfficialize: false,
  canReopen: false,
  publicEnabled: false,
});

export const athleticsScopePath = ({ idevent, idsport }) =>
  `${ATHLETICS_RESULTS_ADMIN_BASE}/events/${Number(idevent)}/sports/${Number(idsport)}`;

/** Un 200 con `ok:false` se trata como error con la misma forma que uno de axios. */
const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === "object" && body.ok === false) {
    const error = new Error(body.message || "Respuesta no válida del servidor.");
    error.response = { status: response?.status ?? 200, data: body };
    throw error;
  }
  return body && typeof body === "object" && "data" in body ? body.data : body;
};

const request = async ({ method, scope, path = "", params, data, signal }) => {
  const config = { method, url: `${athleticsScopePath(scope)}${path}` };
  if (params) config.params = params;
  if (data !== undefined) config.data = data;
  if (signal) config.signal = signal;
  return unwrap(await httpService.request(config));
};

const revisionBody = (expectedRevision) => ({ expectedRevision: Number(expectedRevision) });

/**
 * Carril ocupado y filas con resultado (agregar y mover): `replace` (el ocupante sale de la serie) o `swap` (intercambio),
 * con `replaceRowId` / `swapRowId` cuando el carril tiene dos filas (800/1500), y `confirmResult` para tocar una fila que ya
 * tiene marca o estado. Solo se agregan al cuerpo los que vienen.
 */
const withOccupied = (body, { replace, replaceRowId, swap, swapRowId, confirmResult } = {}) => {
  if (replace === true) body.replace = true;
  if (replaceRowId !== undefined && replaceRowId !== null) body.replaceRowId = Number(replaceRowId);
  if (swap === true) body.swap = true;
  if (swapRowId !== undefined && swapRowId !== null) body.swapRowId = Number(swapRowId);
  if (confirmResult === true) body.confirmResult = true;
  return body;
};

// ------------------------------------------------ estado ------------------------------------------------

export const getAthleticsResultsStatus = async ({ idevent, idsport }) => {
  try {
    const data = (await request({ method: "get", scope: { idevent, idsport }, path: "/status" })) || {};
    return {
      enabled: data.enabled === true,
      reason: typeof data.reason === "string" ? data.reason : null,
      canWrite: data.canWrite === true,
      canOfficialize: data.canOfficialize === true,
      canReopen: data.canReopen === true,
      publicEnabled: data.publicEnabled === true,
    };
  } catch (error) {
    const code = error?.response?.data?.code;
    return { ...ATHLETICS_RESULTS_STATUS_OFF, reason: typeof code === "string" ? code : null };
  }
};

// ------------------------------------------------ ajustes ------------------------------------------------

export const getAthleticsSettings = ({ idevent, idsport }) =>
  request({ method: "get", scope: { idevent, idsport }, path: "/settings" });

/** Campos opcionales: solo viajan los presentes. */
export const updateAthleticsSettings = ({ idevent, idsport, settings }) => {
  const allowed = ["publicTabEnabled", "publicTabLabel", "publicStandingsEnabled", "publicPointsEnabled"];
  const body = {};
  allowed.forEach((key) => {
    if (settings && settings[key] !== undefined) body[key] = settings[key];
  });
  return request({ method: "put", scope: { idevent, idsport }, path: "/settings", data: body });
};

// ------------------------------------------------ pruebas ------------------------------------------------

/** `{ settings, jornadas, pruebas }`. Los filtros del servidor (`q, level, branch, discipline, status, date`) son opcionales. */
export const listAthleticsPruebas = ({ idevent, idsport, filters }) => {
  const params = {};
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") params[key] = value;
  });
  return request({
    method: "get",
    scope: { idevent, idsport },
    path: "/pruebas",
    params: Object.keys(params).length ? params : undefined,
  });
};

export const getAthleticsPrueba = ({ idevent, idsport, pruebaId }) =>
  request({ method: "get", scope: { idevent, idsport }, path: `/pruebas/${Number(pruebaId)}` });

/** Acreditados de la institución de un equipo de posta (cualquier prueba del deporte): `{ teamId, institution, candidates[] }`. */
export const getAthleticsRelayCandidates = ({ idevent, idsport, pruebaId, teamId, signal }) =>
  request({
    method: "get",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/relay-candidates`,
    params: { teamId: Number(teamId) },
    signal,
  });

/** `RoundSave` parcial → `PruebaDetail`. */
export const saveAthleticsRound = ({ idevent, idsport, pruebaId, roundId, payload }) =>
  request({
    method: "put",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/rounds/${Number(roundId)}`,
    data: payload,
  });

/** Agrega a la serie un inscrito (`entryId`) o un equipo de posta (`teamId`) → `PruebaDetail`. */
export const addAthleticsRoundEntry = ({ idevent, idsport, pruebaId, roundId, entryId, teamId, lane, expectedRevision, ...occupied }) => {
  const body = withOccupied(revisionBody(expectedRevision), occupied);
  if (teamId !== undefined && teamId !== null) body.teamId = Number(teamId);
  else body.entryId = Number(entryId);
  // Carril opcional (tablero de carriles): el servidor responde 409 LANE_TAKEN si ya está ocupado.
  if (lane !== undefined && lane !== null && lane !== "") body.lane = Number(lane);
  return request({
    method: "post",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/rounds/${Number(roundId)}/entries`,
    data: body,
  });
};

/** Quita una fila de su serie (papelera). Con marca o estado el servidor exige `confirmResult` (409 REMOVE_NEEDS_CONFIRMATION). */
export const removeAthleticsRoundEntry = ({ idevent, idsport, pruebaId, roundId, rowId, expectedRevision, confirmResult }) =>
  request({
    method: "delete",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/rounds/${Number(roundId)}/entries/${Number(rowId)}`,
    params: confirmResult === true ? { ...revisionBody(expectedRevision), confirmResult: true } : revisionBody(expectedRevision),
  });

/** Acreditados de atletismo que coinciden con `query` (nombre, documento o sigla) para agregarlos a la prueba → `{ candidates }`. */
export const searchAthleticsAthleteCandidates = async ({ idevent, idsport, pruebaId, query }) => {
  const data = await request({
    method: "get",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/athlete-candidates`,
    params: { q: query },
  });
  return Array.isArray(data?.candidates) ? data.candidates : [];
};

/**
 * Agrega un deportista a la prueba = crea su inscripción en la categoría si falta + lo pone en la serie → `PruebaDetail`.
 * Pista: `roundId` y `lane` (el servidor valida que el carril esté libre; 800/1500 admiten dos). Concurso: al final.
 */
export const addAthleticsAthlete = ({ idevent, idsport, pruebaId, idacreditation, roundId, lane, expectedRevision, ...occupied }) => {
  const body = withOccupied({ ...revisionBody(expectedRevision), idacreditation: Number(idacreditation) }, occupied);
  if (roundId !== undefined && roundId !== null && roundId !== "") body.roundId = Number(roundId);
  if (lane !== undefined && lane !== null && lane !== "") body.lane = Number(lane);
  return request({ method: "post", scope: { idevent, idsport }, path: `/pruebas/${Number(pruebaId)}/athletes`, data: body });
};

/**
 * Mueve una fila (deportista o equipo de posta) a otra serie de la misma prueba → `PruebaDetail`. `swap` intercambia con
 * quien ocupa el carril (`swapRowId` si hay dos en 800/1500); `confirmResult` acepta mover una fila que ya tiene marca.
 */
export const moveAthleticsEntry = ({
  idevent,
  idsport,
  pruebaId,
  roundId,
  rowId,
  targetRoundId,
  lane,
  order,
  swap,
  swapRowId,
  replace,
  replaceRowId,
  confirmResult,
  expectedRevision,
}) => {
  const body = withOccupied({ ...revisionBody(expectedRevision), targetRoundId: Number(targetRoundId) }, { swap, swapRowId, replace, replaceRowId, confirmResult });
  if (lane !== undefined && lane !== null && lane !== "") body.lane = Number(lane);
  if (order !== undefined && order !== null && order !== "") body.order = Number(order);
  return request({
    method: "post",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/rounds/${Number(roundId)}/entries/${Number(rowId)}/move`,
    data: body,
  });
};

/**
 * Instituciones que se pueden agregar como equipo a una posta → `{ candidates: [{ institution, athletes, team, placement }] }`
 * (`team` = su equipo en la prueba si existe; `placement` = serie y carril si ya corre).
 */
export const searchAthleticsTeamCandidates = async ({ idevent, idsport, pruebaId }) => {
  const data = await request({ method: "get", scope: { idevent, idsport }, path: `/pruebas/${Number(pruebaId)}/team-candidates` });
  return Array.isArray(data?.candidates) ? data.candidates : [];
};

/** Agrega una institución como equipo de posta (reutiliza su equipo sin serie o crea uno sin relevistas) → `PruebaDetail`. */
export const addAthleticsTeam = ({ idevent, idsport, pruebaId, institutionId, roundId, lane, expectedRevision, ...occupied }) => {
  const body = withOccupied({ ...revisionBody(expectedRevision), institutionId: Number(institutionId) }, occupied);
  if (roundId !== undefined && roundId !== null && roundId !== "") body.roundId = Number(roundId);
  if (lane !== undefined && lane !== null && lane !== "") body.lane = Number(lane);
  return request({ method: "post", scope: { idevent, idsport }, path: `/pruebas/${Number(pruebaId)}/teams`, data: body });
};

/** Agrega una serie (la serie única pasa a Serie 1; la nueva copia N°, horario y cámara) → `PruebaDetail`. */
export const addAthleticsRound = ({ idevent, idsport, pruebaId, expectedRevision }) =>
  request({ method: "post", scope: { idevent, idsport }, path: `/pruebas/${Number(pruebaId)}/rounds`, data: revisionBody(expectedRevision) });

/** Quita una serie vacía y no terminada (las demás se renumeran) → `PruebaDetail`. */
export const removeAthleticsRound = ({ idevent, idsport, pruebaId, roundId, expectedRevision }) =>
  request({
    method: "delete",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/rounds/${Number(roundId)}`,
    params: revisionBody(expectedRevision),
  });

/** Arma (o rehace, si aún no tiene marcas) la final con los 8 mejores tiempos → `PruebaDetail`. */
export const buildAthleticsFinal = ({ idevent, idsport, pruebaId, expectedRevision }) =>
  request({
    method: "post",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/final`,
    data: revisionBody(expectedRevision),
  });

/** `{ startList?, results? }` → `PruebaSummary`. */
export const updateAthleticsVisibility = ({ idevent, idsport, pruebaId, visibility }) => {
  const body = {};
  if (typeof visibility?.startList === "boolean") body.startList = visibility.startList;
  if (["off", "live", "official"].includes(visibility?.results)) body.results = visibility.results;
  return request({
    method: "put",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/visibility`,
    data: body,
  });
};

export const officializeAthleticsPrueba = ({ idevent, idsport, pruebaId, expectedRevision }) =>
  request({
    method: "post",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/official`,
    data: revisionBody(expectedRevision),
  });

export const reopenAthleticsPrueba = ({ idevent, idsport, pruebaId, expectedRevision }) =>
  request({
    method: "post",
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/reopen`,
    data: revisionBody(expectedRevision),
  });

// ---------------------------------------------- clasificación ----------------------------------------------

export const getAthleticsStandings = ({ idevent, idsport }) =>
  request({ method: "get", scope: { idevent, idsport }, path: "/standings" });

// ------------------------------------- descargas (planilla PDF y FinishLynx) -------------------------------------
// Siempre datos frescos: cada descarga vuelve a pedir el archivo (`Cache-Control: no-store`); el backend lee la base en
// el momento. Devuelven `{ blob, filename }` sin disparar la descarga (la hace `saveAthleticsFile`).

/** Nombre del archivo desde Content-Disposition (`filename*` UTF-8 primero); `fallback` si no viene. */
export const filenameFromDisposition = (disposition, fallback) => {
  const header = String(disposition || "");
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch (_error) {
      // cae al nombre ASCII
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : fallback;
};

/** Un error de una petición `blob` trae el cuerpo JSON como Blob: se convierte para que la interfaz lea `code`/`message`. */
const unwrapBlobError = async (error) => {
  const data = error?.response?.data;
  if (data && typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      error.response.data = JSON.parse(await data.text());
    } catch (_parseError) {
      error.response.data = {};
    }
  }
  return error;
};

const requestFile = async ({ scope, path, params, signal, fallbackName }) => {
  try {
    const response = await httpService.get(`${athleticsScopePath(scope)}${path}`, {
      params,
      responseType: "blob",
      signal,
      headers: { "Cache-Control": "no-store" },
    });
    return {
      blob: response.data,
      filename: filenameFromDisposition(response.headers?.["content-disposition"], fallbackName),
    };
  } catch (error) {
    throw await unwrapBlobError(error);
  }
};

const phaseParams = (phase) => (phase === "final" || phase === "series" ? { phase } : undefined);

/** Planilla PDF de una prueba en su fase (`series` | `final`). */
export const downloadAthleticsPlanilla = ({ idevent, idsport, pruebaId, phase, signal }) =>
  requestFile({
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/sheets/planilla.pdf`,
    params: phaseParams(phase),
    signal,
    fallbackName: "planilla.pdf",
  });

/** lynx_<N°>.evt / .sch de una prueba de pista en su fase. */
export const downloadAthleticsPruebaLynx = ({ idevent, idsport, pruebaId, phase, kind }) =>
  requestFile({
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/sheets/lynx.${kind === "sch" ? "sch" : "evt"}`,
    params: phaseParams(phase),
    fallbackName: `lynx.${kind === "sch" ? "sch" : "evt"}`,
  });

/** Reporte PDF del avance de la combinada (decatlón/heptatlón), pedido desde la combinada o una de sus pruebas. */
export const downloadAthleticsCombinedReport = ({ idevent, idsport, pruebaId }) =>
  requestFile({
    scope: { idevent, idsport },
    path: `/pruebas/${Number(pruebaId)}/sheets/combinada.pdf`,
    fallbackName: "combinada.pdf",
  });

/**
 * Cómputo general de puntos (PDF): la clasificación por universidad con el detalle de cada total y el anexo de combinadas.
 * El servidor no lo genera si el detalle de alguna institución no suma su total (409 COMPUTO_MISMATCH con el mensaje).
 */
export const downloadAthleticsComputo = ({ idevent, idsport }) =>
  requestFile({
    scope: { idevent, idsport },
    path: "/sheets/computo-general.pdf",
    fallbackName: "Computo_General_Atletismo.pdf",
  });

/** FinishLynx completo (todas las pruebas de pista): lynx.evt / lynx.sch. */
export const downloadAthleticsEventLynx = ({ idevent, idsport, kind }) =>
  requestFile({
    scope: { idevent, idsport },
    path: `/sheets/lynx.${kind === "sch" ? "sch" : "evt"}`,
    fallbackName: `lynx.${kind === "sch" ? "sch" : "evt"}`,
  });

/**
 * FinishLynx de varias pruebas: .zip con lynx.evt y lynx.sch unidos. `ids` = ["12:series", "12:final", "40", ...]
 * (id de prueba o sub-prueba, con fase opcional); el servidor los ordena por N° de horario.
 */
export const downloadAthleticsLynxZip = ({ idevent, idsport, ids }) =>
  requestFile({
    scope: { idevent, idsport },
    path: "/sheets/lynx.zip",
    params: { ids: (Array.isArray(ids) ? ids : []).join(",") },
    fallbackName: "FinishLynx.zip",
  });

/** Guarda un Blob con su nombre (descarga del navegador). */
export const saveAthleticsFile = ({ blob, filename }) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};
